/**
 * Wishlist Manager - LocalStorage based wishlist for Shopify
 */
class WishlistManager {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.items = this.load();
    this.init();
  }

  init() {
    this.updateUI();
    this.attachEventListeners();
  }

  load() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error loading wishlist:', e);
      return [];
    }
  }

  save() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.items));
      this.updateUI();
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  add(productId, productData) {
    if (!this.has(productId)) {
      this.items.push({
        id: productId,
        handle: productData.handle,
        title: productData.title,
        price: productData.price,
        image: productData.image,
        url: productData.url,
        addedAt: Date.now()
      });
      this.save();
      return true;
    }
    return false;
  }

  remove(productId) {
    this.items = this.items.filter(item => item.id !== productId);
    this.save();
  }

  has(productId) {
    return this.items.some(item => item.id === productId);
  }

  toggle(productId, productData) {
    if (this.has(productId)) {
      this.remove(productId);
      return false;
    } else {
      this.add(productId, productData);
      return true;
    }
  }

  getAll() {
    return this.items;
  }

  count() {
    return this.items.length;
  }

  clear() {
    this.items = [];
    this.save();
  }

  updateUI() {
    // Update wishlist count in header
    const countElements = document.querySelectorAll('[data-wishlist-count]');
    countElements.forEach(el => {
      el.textContent = this.count();
      el.style.display = this.count() > 0 ? 'flex' : 'none';
    });

    // Update all wishlist buttons
    const buttons = document.querySelectorAll('[data-wishlist-button]');
    buttons.forEach(btn => {
      const productId = btn.dataset.productId;
      const isInWishlist = this.has(productId);
      
      btn.classList.toggle('active', isInWishlist);
      btn.setAttribute('aria-label', isInWishlist ? 'Remove from wishlist' : 'Add to wishlist');
      
      // Update SVG fill
      const svg = btn.querySelector('svg path');
      if (svg) {
        if (isInWishlist) {
          svg.setAttribute('fill', '#890000');
          svg.setAttribute('stroke', '#890000');
        } else {
          svg.setAttribute('fill', 'none');
          svg.setAttribute('stroke', 'currentColor');
        }
      }
    });
  }

  attachEventListeners() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-button]');
      if (!btn) return;

      e.preventDefault();
      
      const productId = btn.dataset.productId;
      const productData = {
        handle: btn.dataset.productHandle,
        title: btn.dataset.productTitle || document.querySelector('.product__title h1')?.textContent.trim(),
        price: btn.dataset.productPrice || document.querySelector('.price-item--regular')?.textContent.trim(),
        image: btn.dataset.productImage || document.querySelector('.product__media img')?.src,
        url: btn.dataset.productUrl || window.location.pathname
      };

      const isAdded = this.toggle(productId, productData);
      this.showNotification(isAdded ? 'Added to wishlist' : 'Removed from wishlist');
    });
  }

  showNotification(message) {
    // Remove existing notification
    const existing = document.querySelector('.wishlist-notification');
    if (existing) existing.remove();

    // Create notification
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      bottom: 30px;
      right: 30px;
      background: #890000;
      color: #F7EBE1;
      padding: 16px 24px;
      border-radius: 8px;
      font-family: 'Visby', sans-serif;
      font-size: 14px;
      font-weight: 600;
      z-index: 10000;
      animation: slideIn 0.3s ease;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 2000);
  }
}

// Add notification animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(400px);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);

// Initialize wishlist when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlist = new WishlistManager();
  });
} else {
  window.wishlist = new WishlistManager();
}
